import { useEffect, useState } from 'react';
import { Header, HeaderPillButton } from '@/components/DashboardComponents'
import { Button, RoundIconButton, Textbox, Spacing, RadioGroup, RadioOption } from '@/form/FormComponents'
import { useNavigate, useLoaderData, useRouteLoaderData, useParams, useRevalidator } from 'react-router'
import { GenericEmptyListPlaceholder } from '@/DashboardLayout'
import { useModal, Modal, ModalContent, ModalButtons, ModalListItem, useErrorModal } from '@/components/Modal'
import { useMenu } from '@/components/Menu'
import { Icon } from '@/components/Icon'
import { FormProvider, FormSubmitProvider, FormDataProvider } from '@/form/FormProvider'
import { renameScenario } from '@/data/ScenarioData'
import { useUser } from '@/systems/Authentication'
import { publicAction } from '@/data/loaders'
import { List, ListItem, ItemColumn, ItemActions } from '@/components/List'
import { useInmemoryStorage, usePreference } from '@/hooks/useStorage';
import { useShareModal } from '@/components/FilesystemShareModals';
import { useMoveModal } from '@/components/FilePickerModals';
import { FileLocation } from '@/form/FormComponents';
import { shortDate } from '@/shared/utils';

export function Filesystem({ view: view_prop }) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { folder_id } = useParams();
  const { sample_scenarios, value: files, folder } = useLoaderData().data;
  const { userdata, uid } = useUser();
  const view = folder ? folder.view : (view_prop || "scenarios");

  const [show_sample_scenarios, setShowSampleScenarios] = useInmemoryStorage("show_sample_scenarios", files.length < 2 || userdata.email === "demo@sapientclinician.com");

  const [sort, setSort] = usePreference(`${view}_sort`, [view === "shared" ? "date_shared" : (view === "trash" ? "date_trashed" : "date_updated"), -1]); // [column id, sort value]
  const [folders, setFolders] = usePreference(`${view}_folders`, true);
  const [,setDateLastCheckedSharedWithMe] = usePreference("date_last_checked_shared_with_me", 0);

  useEffect(() => {
    if (!folder && view === "shared") {
      setDateLastCheckedSharedWithMe(Date.now());
    }
  }, [folder, files, view, setDateLastCheckedSharedWithMe]);

  const [openErrorModal] = useErrorModal();

  const [openNewFolderModal] = useNewFolderModal();

  const handleNewFolder = () => {
    openNewFolderModal({ folder_id, view, callback: () => {
      revalidator.revalidate();
    }});
  }

  const [openCreateInSharedFolderModal] = useModal(function ({ context, closeModal, setLoading }) {
    function handleSubmit() {
      closeModal();
      context.callback();
    }

    return (
      <Modal title="Create in a shared folder?">
        <ModalContent>
          {`The ${context.item_name || "created item"} will be shared with the same people as this folder.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Create and share" className="primary" onClick={handleSubmit}/>
        </ModalButtons>
      </Modal>
    )
  })

  function handleCreate(v) {
    if (folder && folder._is_shared) {
      openCreateInSharedFolderModal({ 
        item_name: v === "scenarios" ? "scenario" : "rubric",
        callback: () => {
          navigate(`/${v}/new/edit` + (folder_id ? `?folder_id=${folder_id}` : ""));
        }
      });
    } else {
      navigate(`/${v}/new/edit` + (folder_id ? `?folder_id=${folder_id}` : ""));
    }
  }

  const [openClearTrashModal] = useModal(({ closeModal, setLoading }) => {
    function submitClearTrash() {
      setLoading(true);
      
      publicAction("deleteTrashedFiles", {}).then(function() {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }
    
    return (
      <Modal title="Delete forever?">
        <ModalContent>
          {`All items in the trash will be deleted forever. You can't undo this.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Delete forever" className="destructive" onClick={submitClearTrash}/>
        </ModalButtons>
      </Modal>
    )
  });

  const { handleRestore, is_restoring } = useHandleRestore({ file: folder });

  const banner = view === "trash" ? (
    <div className="callout_hint_box">
      <div>
        <div><Icon name="trash-can-outline"/></div>
        <div>Items in the trash will be deleted forever after 30 days.</div>
        <div className="callout_small_button">
          <Button text="Empty trash" className="hollow small" onClick={openClearTrashModal} disabled={files.length == 0}/>
        </div>
      </div>
    </div>
  ) : (
    folder?.trashed && (
      <div className="callout_hint_box">
        <div>
          <div><Icon name="trash-can-outline"/></div>
          <div>This folder is in the trash. Items that are crossed out will be permanently deleted soon, and the rest will be relocated.</div>
          {(folder.trashed_head && folder.uid === uid) && <div className="callout_small_button">
            <Button text="Restore" icon="history" className="hollow small" onClick={handleRestore} loading={is_restoring}/>
          </div>}
        </div>
      </div>
    )
  )

  const { root, path } = getRootAndPath({ folder, uid });

  const { handleOnClick: handleOnClickExpandHistory } = useMenu({
    menuItems: path.map((f) => {
      return {
        label: f.title,
        icon: f._is_shared ? "folder-account" : "folder",
        onClick: () => {
          navigate(`/folders/${f._id}`);
        }
      }
    }),
    rightHanded: false
  });

  const [handleRename] = useRenameFolderModal();
  const [handleShare] = useShareModal();
  const { moveMenuItem } = useMove({ file: folder, folder: folder?.parent && (folder.ancestor_docs || []).find(a => a._id === folder.parent), view });
  const { trashMenuItem } = useTrash({ file: folder, folder: folder?.parent && (folder.ancestor_docs || []).find(a => a._id === folder.parent), view, dont_show_remove_option: true, go_back_upon_trash: true });

  const { handleOnClick } = useMenu({
    menuItems: folder ? [
      {
        label: 'Rename',
        icon: 'rename-outline',
        onClick: () => {handleRename({
          folder,
          callback: () => {
            revalidator.revalidate();
          }
        })},
        disabled: !["edit", "owner"].includes(folder._level),
      },
      "separator",
      {
        label: 'Share',
        icon: 'account-plus-outline',
        onClick: () => {handleShare(folder)},
        disabled: !["edit", "owner"].includes(folder._level),
      },
      ...moveMenuItem,
      ...(trashMenuItem.length > 0 ? ["separator"] : []),
      ...trashMenuItem
    ] : [],
    rightHanded: false
  });

  const folder_title = folder && <>
    <HeaderPillButton text={folder.title} dropdown_icon={true} onClick={handleOnClick} onContextMenu={handleOnClick}/>
    {folder._is_shared && <RoundIconButton icon="account-multiple-outline" className="small small_icon header_title_share_icon" onClick={() => {handleShare(folder)}} disabled={!["edit", "owner"].includes(folder._level)}/>}
  </>

  const breadcrumbs = folder && <>
    <HeaderPillButton text={root === "shared" ? "Shared with me" : (root === "trash" ? "Trash" : `My ${root}`)} to={`/${root}`}/>
    <Icon name="chevron-right" className="header_title_chevron"/>
    {path.length === 1 && <HeaderPillButton text={path[0].title} to={`/folders/${path[0]._id}`}/>}
    {path.length > 1 && <RoundIconButton icon="dots-horizontal" className="small header_title_more_icon" onClick={handleOnClickExpandHistory} onContextMenu={handleOnClickExpandHistory}/>}
    {path.length > 0 && <Icon name="chevron-right" className="header_title_chevron"/>}
    {folder_title}
  </>;

  const disable_buttons = folder && (!["edit", "owner"].includes(folder._level) || folder.trashed);

  return (
    <>
      <Header title={folder ? folder_title : (view === "shared" ? "Shared with me" : (view === "trash" ? "Trash" : `My ${view}`))} breadcrumbs={breadcrumbs} no_shadow={true} backButton={folder ? true : false}>
        {view === "scenarios" && <Button className="primary" icon="plus" text="New scenario" onClick={() => handleCreate("scenarios")} disabled={disable_buttons}/>}
        {view === "rubrics" && <Button className="primary" icon="plus" text="New rubric" onClick={() => handleCreate("rubrics")} disabled={disable_buttons}/>}
        {(view === "scenarios" || view === "rubrics") && <Button icon="folder-plus-outline" text="New folder" onClick={handleNewFolder} disabled={disable_buttons}/>}
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container">
        <FileBrowserList files={files} view={view} sort={sort} setSort={setSort} folders={folders} setFolders={setFolders} folder={folder} banner={banner}/>

        {sample_scenarios?.length > 0 ? <>
          <div className="toggle_expand_header" onClick={() => setShowSampleScenarios(!show_sample_scenarios)} tabIndex={0}><div>Sample case scenarios</div><div><Icon name={show_sample_scenarios ? "chevron-up" : "chevron-down"}/></div></div>
          {show_sample_scenarios && <List fullPage={true} actions={true} actions_width={228} hide_header={true} columns={[{}]}>
            {sample_scenarios.map((scenario) => <ScenarioItem key={scenario._id} file={scenario} is_sample={true}/>)}
          </List>}
        </> : null}
      </div>
    </>
  )
}

export function getRootAndPath({ folder, uid }) {
  let root = false;
  let path = [];

  if (folder) {
    if (folder.trashed_head && folder.uid === uid) {
      root = "trash";
    } else if (folder.parent) {
      let current = folder.parent;
      root = "shared";
      while (true) {
        let ancestor = folder.ancestor_docs.find(a => a._id == current);
        if (ancestor) {
          path.unshift(ancestor);
          if (ancestor.trashed_head && ancestor.uid === uid) {
            root = "trash";
            break;
          } else if (ancestor.parent === null) {
            if (ancestor.root_uid === uid) {
              root = folder.view;
            }
            break;
          } else {
            current = ancestor.parent;
          }
        } else {
          break;
        }
      }
    } else {
      if (folder.root_uid === uid) {
        root = folder.view;
      } else {
        root = "shared";
      }
    }
  }

  return { root, path };
}

export function FileBrowserList({ filePicker, files, view, sort, setSort, folders, setFolders, folder, banner }) {
  const can_create_folders_here = (folder && ["edit", "owner"].includes(folder._level)) || (!folder && (view === "scenarios" || view === "rubrics"));

  return (
    <>
      <List fullPage={true} actions={true} actions_width={228} hide_actions={view === "rubrics" || view === "trash" || filePicker} columns={[
        { name: "Title", id: "title", sortable: [{ value: -1, name: "A to Z", arrow: "up", default: true }, { value: 1, name: "Z to A", arrow: "down" }] },
        { name: "Shared by", id: "shared_by", width: 240, hide_order: 4, sortable: [{ value: -1, name: "A to Z", arrow: "up", default: true }, { value: 1, name: "Z to A", arrow: "down" }], disabled: view !== "shared" },
        { name: "Date created", id: "date_created", width: 105, hide_order: 1, sortable_group: "date", sortable: [{ value: -1, name: "New to old", arrow: "down", default: true }, { value: 1, name: "Old to new", arrow: "up" }] },
        { name: "Date updated", id: "date_updated", width: 105, hide_order: 2, sortable_group: "date", sortable: [{ value: -1, name: "New to old", arrow: "down", default: true }, { value: 1, name: "Old to new", arrow: "up" }] },
        { name: "Date shared", id: "date_shared", width: 105, hide_order: 3, sortable_group: "date", sortable: [{ value: -1, name: "New to old", arrow: "down", default: true }, { value: 1, name: "Old to new", arrow: "up" }], disabled: view !== "shared" },
        { name: "Date trashed", id: "date_trashed", width: 105, hide_order: 3, sortable_group: "date", sortable: [{ value: -1, name: "New to old", arrow: "down", default: true }, { value: 1, name: "Old to new", arrow: "up" }], disabled: view !== "trash" },
        { name: "Assigned to", width: 170, hide_order: 5, disabled: view !== "scenarios" },
        { name: "Original location", width: 200, hide_order: 0, disabled: view !== "trash" },
      ]} sort={sort} setSort={setSort} folders={folders} setFolders={(view !== "shared" && view !== "trash") ? setFolders : undefined} banner={banner}>
        {files.sort((a, b) => {
          if (folders && view !== "shared" && view !== "trash") {
            if (a.type == "folder" && b.type !== "folder") {
              return -1;
            } else if (a.type !== "folder" && b.type == "folder") {
              return 1;
            }
          }
          if (sort[0] === "title") {
            return sort[1] === -1 ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
          } else if (sort[0] === "date_updated") {
            return sort[1] === -1 ? b.date_modified - a.date_modified : a.date_modified - b.date_modified;
          } else if (sort[0] === "date_created") {
            return sort[1] === -1 ? b.date_created - a.date_created : a.date_created - b.date_created;
          } else if (sort[0] === "date_shared") {
            return sort[1] === -1 ? b._date_shared - a._date_shared : a._date_shared - b._date_shared;
          } else if (sort[0] === "date_trashed") {
            return sort[1] === -1 ? b.date_trashed - a.date_trashed : a.date_trashed - b.date_trashed;
          } else if (sort[0] === "shared_by") {
            return sort[1] === -1 ? (b._sharer_email || "zzzzzzzzzzzz").localeCompare(a._sharer_email || "zzzzzzzzzzzz") : (a._sharer_email || "zzzzzzzzzzzz").localeCompare(b._sharer_email || "zzzzzzzzzzzz");
          }
        }).map((file) => {
          if (file.type == "scenario") {
            return <ScenarioItem key={file._shortcut_id || file._id} file={file} view={view} filePicker={filePicker} can_create_folders_here={can_create_folders_here} folder={folder} />
          } else if (file.type == "folder") {
            return <FolderItem key={file._shortcut_id || file._id} file={file} view={view} filePicker={filePicker} can_create_folders_here={can_create_folders_here} folder={folder} />
          } else if (file.type == "rubric") {
            return <RubricItem key={file._shortcut_id || file._id} file={file} view={view} filePicker={filePicker} can_create_folders_here={can_create_folders_here} folder={folder} />
          } else {
            return <ShortcutItem key={file._id} file={file} view={view} filePicker={filePicker} can_create_folders_here={can_create_folders_here} folder={folder} />
          }
        })}
      </List>
      {files.length == 0 &&
        (folder ? <GenericEmptyListPlaceholder text="Folder is empty" icon="folder-outline" /> : <>
          {view === "scenarios" && <GenericEmptyListPlaceholder text={`You haven't created any scenarios yet`} inline={true} />}
          {view === "rubrics" && <GenericEmptyListPlaceholder text={`You haven't created any rubrics yet`} icon="bullseye-arrow" />}
          {view === "shared" && <GenericEmptyListPlaceholder text={`Nothing shared with you yet`} icon="account-multiple-outline" />}
          {view === "trash" && <GenericEmptyListPlaceholder text={`Nothing in trash`} icon="trash-can-outline" />}
        </>)
      }
    </>
  )
}

export const useCopyScenarioModal = () => {
  const [openCopyScenarioModal] = useModal(function ({ context, closeModal, setLoading }) {
    const { file, callback, show_choose_location = false, append_copy_to_title = true } = context;

    function submitRenameScenario({formatted_data, focusError}) {
      setLoading(true);
  
      publicAction("duplicateScenario", { id: file.item_id, title: formatted_data.title, ...(show_choose_location ? { parent: formatted_data.parent?._id || null } : {}) }).then(function({ data }) {
        const { folder_id, file_id, item_id } = data;
        closeModal();
        callback({ folder_id, file_id, item_id });
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="Make a copy of scenario">
        <FormProvider onSubmit={submitRenameScenario}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox title="New scenario title" name="title" onEnter={handleSubmit} default_value={append_copy_to_title ? "Copy of "+file.title : file.title} required="Please enter a scenario title" />
                  {show_choose_location && <>
                    <Spacing/>
                    <FileLocation name="parent" title="Choose where to save" type="scenario" />
                  </>}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  return [openCopyScenarioModal];
}

export const useRenameScenarioModal = () => {
  const [openRenameScenarioModal] = useModal(function ({ context, closeModal, setLoading }) {
    const { file, callback } = context;

    function submitRenameScenario({formatted_data, focusError}) {
      setLoading(true);
  
      renameScenario({ id: file.item_id, title: formatted_data.title }).then(function(data) {
        closeModal();
        callback();
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="Rename scenario">
        <FormProvider onSubmit={submitRenameScenario}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="title" onEnter={handleSubmit} default_value={file.title} required="Please enter a scenario title" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  return [openRenameScenarioModal];
}

export const useAssignScenarioModal = ({ courses }) => {
  const navigate = useNavigate();

  const educator_courses = courses.filter((course) => course.membership_type == "educator");

  const [openAssignScenarioModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal title="Choose course to assign to">
        <ModalContent>
          {educator_courses.length > 0 ? (
            [...educator_courses].sort((a, b) => a.name.localeCompare(b.name)).map((course) => <ModalListItem key={course._id} icon="book-open-variant-outline" text={course.name+(course.section_name ? ` (${course.section_name})` : "")} onClick={() => {closeModal();navigate(`/courses/${course._id}/assessments/new/${context.assign_scenario_id}`);}}/>)
          ) : "You aren't a teacher in any courses."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  return [openAssignScenarioModal];
}

function ScenarioItem({file, view, is_sample = false, filePicker, can_create_folders_here, folder}) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const courses = useRouteLoaderData("dashboard_root").data.value.filter((course) => !course.archived);

  const [openAssignScenarioModal] = useAssignScenarioModal({ courses });

  function handleAssignScenario() {
    openAssignScenarioModal({ assign_scenario_id: file.item_id });
  }

  const [openRenameScenarioModal] = useRenameScenarioModal();

  function handleRenameScenario() {
    openRenameScenarioModal({ file, callback: () => {
      revalidator.revalidate();
    } });
  }

  const [openCopyScenarioModal] = useCopyScenarioModal();

  function handleCopyScenario() {
    openCopyScenarioModal({ file, show_choose_location: !can_create_folders_here || is_sample, append_copy_to_title: !!(can_create_folders_here && !is_sample), callback: () => {
      revalidator.revalidate();
    } });
  }

  const [handleShare] = useShareModal();
  const { moveMenuItem } = useMove({ file, folder, view });
  const { trashMenuItem, trashedMenuItems } = useTrash({ file, folder, view });
  const { addToMyAction, addToMyMenuItem, shareRemoveReportMenuItems } = useSharedWithMe({ file, is_shared: view === "shared" });

  const menu = useMenu({
    menuItems: is_sample ? (
      [
        {
          label: 'Try it out',
          icon: 'play-outline',
          onClick: () => navigate(`/scenarios/${file.item_id}/preview`),
        },
        "separator",
        {
          label: 'Make a copy',
          icon: 'file-copy',
          onClick: handleCopyScenario,
        },
      ]
    ) : (
      trashedMenuItems || [
        ...addToMyMenuItem,
        // {
        //   label: 'Edit',
        //   icon: 'pencil-outline',
        //   onClick: () => navigate(`/scenarios/${file.item_id}`),
        // },
        {
          label: 'Preview',
          icon: 'play-outline',
          onClick: () => navigate(`/scenarios/${file.item_id}/preview`),
        },
        {
          label: 'Assign',
          icon: 'assignment-plus',
          onClick: handleAssignScenario,
          disabled: file.trashed,
        },
        "separator",
        ...(file._is_shortcut ? [{
          heading: "Original scenario",
        }] : []),
        {
          label: 'Rename',
          icon: 'rename-outline',
          onClick: handleRenameScenario,
          disabled: !["edit", "owner"].includes(file._level),
        },
        {
          label: 'Make a copy',
          icon: 'file-copy',
          onClick: handleCopyScenario,
        },
        ...(!file._is_shortcut ? ["separator"] : []),
        {
          label: 'Share',
          icon: 'account-plus-outline',
          onClick: () => handleShare(file),
          disabled: !["edit", "owner"].includes(file._level),
        },
        ...(file._is_shortcut ? ["separator", {
          heading: "Shortcut options",
        }] : []),
        ...moveMenuItem,
        ...(!file._is_shortcut ? ["separator"] : []),
        ...trashMenuItem,
        ...shareRemoveReportMenuItems
      ]
    )
  });

  const assigned_to_course_ids = [...new Set((file.linked_assessments || []).filter(a => a.in_sync != null ? a.in_sync : true).map(a => a.course_id).reverse())].reverse(); // Remove the duplicates, keeping the latest index of each instead of the earliest index
  const assigned_to_course_names = assigned_to_course_ids.map(course_id => courses.find(course => course._id == course_id)?.name);
  const linked_course_name = assigned_to_course_names.filter(name => name).pop() || null;

  return (
    <ListItem icon="forum-outline" iconColor="var(--scenario_file_color)" iconShortcut={file._is_shortcut} onClick={is_sample ? (event) => {menu.handleContextMenu(event)} : () => {filePicker ? filePicker.handleClick(file) : navigate("/scenarios/"+file.item_id)}} menu={menu} hideMenu={!!filePicker} disabled={filePicker && (filePicker.type !== "scenario" || filePicker.disable_files)}>
      <ItemColumn className={"with_share_icon" + ((file.trashed && view !== "trash") ? " crossed_out" : "")}><span>{file.title}</span>{file._is_shared && <Icon name="account-multiple-outline" className="is_shared_icon"/>}</ItemColumn>
      {(!is_sample) && <ItemColumn className="item_shared_by">{file._date_shared && (<><Icon name="account-circle-outline" className="shared_by_icon"/><span>{(file._sharer_is_same_org ? file._sharer_name : file._sharer_email) || "(Deleted user)"}</span></>)}</ItemColumn>}
      {(!is_sample) && <ItemColumn>{(shortDate(file.date_created))}</ItemColumn>}
      {(!is_sample) && <ItemColumn>{(shortDate(file.date_modified))}</ItemColumn>}
      {(!is_sample) && <ItemColumn>{file._date_shared && (shortDate(file._date_shared))}</ItemColumn>}
      {(!is_sample) && <ItemColumn>{file.date_trashed && (shortDate(file.date_trashed))}</ItemColumn>}
      {(!is_sample) && <ItemColumn className="item_pills">{
        assigned_to_course_ids.length > 0 ? (
          linked_course_name ? 
            (<><div className="linked_course_pill"><div><Icon name="book-open-variant-outline"/></div><div>{linked_course_name}</div></div>{assigned_to_course_ids.length > 1 ? ` +${assigned_to_course_ids.length-1}` : ""}</>) : 
            `${assigned_to_course_ids.length} course${assigned_to_course_ids.length > 1 ? "s" : ""}`
          )
        : "—"
      }</ItemColumn>}
      {(!is_sample) && <ItemColumn>{view === "trash" && <OriginalLocation file={file} />}</ItemColumn>}
      <ItemActions>
        {addToMyAction ? addToMyAction : <>
          <Button backgroundColor={"var(--header)"} icon="play-outline" text={is_sample ? "Try it out" : "Preview"} className="small hollow" onClick={(event) => {event.stopPropagation(); navigate("/scenarios/"+file.item_id+"/preview")}} />
          {is_sample ? (
            <Button backgroundColor={"var(--header)"} icon="copy" text="Make a copy" className="small hollow" onClick={(event) => {event.stopPropagation(); handleCopyScenario()}} />
          ) : (
            <Button backgroundColor={"var(--header)"} icon="assignment-plus" text="Assign" className="small hollow" onClick={(event) => {event.stopPropagation(); handleAssignScenario()}} disabled={file.trashed} />
          )}
        </>}
      </ItemActions>
    </ListItem>
  )
}

export const useRenameFolderModal = () => {

  const [handleRename] = useModal(function ({ context, closeModal, setLoading }) {
    const { folder, callback } = context;

    function handleSubmit({formatted_data, focusError}) {
      setLoading(true);
  
      publicAction("renameFolder", { id: folder._id, title: formatted_data.title }).then(function(data) {
        closeModal();
        callback();
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="Rename folder">
        <FormProvider onSubmit={handleSubmit}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="title" onEnter={handleSubmit} default_value={folder.title} required="Please enter a title" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  return [handleRename];
}

function FolderItem({file, view, filePicker, folder}) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [openRenameFolderModal] = useRenameFolderModal();

  const handleRename = () => {
    openRenameFolderModal({
      folder: file,
      callback: () => {
        revalidator.revalidate();
      }
    })
  }

  const [handleShare] = useShareModal();
  const { moveMenuItem } = useMove({ file, folder, view });
  const { trashMenuItem, trashedMenuItems } = useTrash({ file, folder, view });
  const { addToMyAction, addToMyMenuItem, shareRemoveReportMenuItems } = useSharedWithMe({ file, is_shared: view === "shared" });

  const menu = useMenu({
    menuItems: trashedMenuItems || [
      ...addToMyMenuItem,
      ...(file._is_shortcut ? [{
          heading: "Original folder",
        }] : []),
      {
        label: 'Rename',
        icon: 'rename-outline',
        onClick: handleRename,
        disabled: !["edit", "owner"].includes(file._level),
      },
      ...(!file._is_shortcut ? ["separator"] : []),
      {
        label: 'Share',
        icon: 'account-plus-outline',
        onClick: () => {handleShare(file)},
        disabled: !["edit", "owner"].includes(file._level),
      },
      ...(file._is_shortcut ? ["separator", {
        heading: "Shortcut options",
      }] : []),
      ...moveMenuItem,
      ...(!file._is_shortcut ? ["separator"] : []),
      ...trashMenuItem,
      ...shareRemoveReportMenuItems
    ]
  });

  return (
    <ListItem icon={file._is_shared ? "folder-account" : "folder"} iconShortcut={file._is_shortcut} onClick={() => {filePicker ? filePicker.handleClick(file) : navigate(`/folders/${file._id}`)}} menu={menu} hideMenu={!!filePicker} disabled={filePicker && ((filePicker.type+"s") !== file.view || filePicker.moving_id === file._id)}>
      <ItemColumn className={((file.trashed && view !== "trash") ? "crossed_out" : "")}>{file.title}</ItemColumn>
      <ItemColumn className="item_shared_by">{file._date_shared && (<><Icon name="account-circle-outline" className="shared_by_icon"/><span>{(file._sharer_is_same_org ? file._sharer_name : file._sharer_email) || "(Deleted user)"}</span></>)}</ItemColumn>
      <ItemColumn>{(shortDate(file.date_created))}</ItemColumn>
      <ItemColumn>{(shortDate(file.date_modified))}</ItemColumn>
      <ItemColumn>{file._date_shared && (shortDate(file._date_shared))}</ItemColumn>
      <ItemColumn>{file.date_trashed && (shortDate(file.date_trashed))}</ItemColumn>
      <ItemColumn></ItemColumn>
      <ItemColumn>{view === "trash" && <OriginalLocation file={file} />}</ItemColumn>
      <ItemActions>{addToMyAction}</ItemActions>
    </ListItem>
  )
}

export const useCopyRubricModal = () => {
  const [openCopyRubricModal] = useModal(function ({ context, closeModal, setLoading }) {
    const { file, callback, show_choose_location = false, append_copy_to_title = true } = context;

    function submitRenameRubric({formatted_data, focusError}) {
      setLoading(true);
  
      publicAction("duplicateRubric", { id: file.item_id, title: formatted_data.title, ...(show_choose_location ? { parent: formatted_data.parent?._id || null } : {}) }).then(function({ data }) {
        const { folder_id, file_id, item_id } = data;
        closeModal();
        callback({ folder_id, file_id, item_id });
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="Make a copy of rubric">
        <FormProvider onSubmit={submitRenameRubric}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox title="New rubric title" name="title" onEnter={handleSubmit} default_value={append_copy_to_title ? "Copy of "+file.title : file.title} required="Please enter a rubric title" />
                  {show_choose_location && <>
                    <Spacing/>
                    <FileLocation name="parent" title="Choose where to save" type="rubric" />
                  </>}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  return [openCopyRubricModal];
}

export const useRenameRubricModal = () => {
  const [openRenameRubricModal] = useModal(function ({ context, closeModal, setLoading }) {
    const { file, callback } = context;

    function submitRenameRubric({formatted_data, focusError}) {
      setLoading(true);
  
      publicAction("renameRubric", { id: file.item_id, title: formatted_data.title }).then(function(data) {
        closeModal();
        callback();
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="Rename rubric">
        <FormProvider onSubmit={submitRenameRubric}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="title" onEnter={handleSubmit} default_value={file.title} required="Please enter a rubric title" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  return [openRenameRubricModal];
}

function RubricItem({file, view, filePicker, can_create_folders_here, folder}) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [openRenameRubricModal] = useRenameRubricModal();

  function handleRenameRubric() {
    openRenameRubricModal({ file, callback: () => {
      revalidator.revalidate();
    } });
  }

  const [openCopyRubricModal] = useCopyRubricModal();

  function handleCopyRubric() {
    openCopyRubricModal({ file, show_choose_location: !can_create_folders_here, append_copy_to_title: can_create_folders_here, callback: () => {
      revalidator.revalidate();
    } });
  }

  const [handleShare] = useShareModal();
  const { moveMenuItem } = useMove({ file, folder, view });
  const { trashMenuItem, trashedMenuItems } = useTrash({ file, folder, view });
  const { addToMyAction, addToMyMenuItem, shareRemoveReportMenuItems } = useSharedWithMe({ file, is_shared: view === "shared" });

  const menu = useMenu({
    menuItems: trashedMenuItems || [
      ...addToMyMenuItem,
      // {
      //   label: 'Edit',
      //   icon: 'pencil-outline',
      //   onClick: () => navigate(`/rubrics/${file.item_id}`),
      // },
      // "separator",
      ...(file._is_shortcut ? [{
        heading: "Original rubric",
      }] : []),
      {
        label: 'Rename',
        icon: 'rename-outline',
        onClick: handleRenameRubric,
        disabled: !["edit", "owner"].includes(file._level),
      },
      {
        label: 'Make a copy',
        icon: 'file-copy',
        onClick: handleCopyRubric,
      },
      ...(!file._is_shortcut ? ["separator"] : []),
      {
        label: 'Share',
        icon: 'account-plus-outline',
        onClick: () => {handleShare(file)},
        disabled: !["edit", "owner"].includes(file._level),
      },
      ...(file._is_shortcut ? ["separator", {
        heading: "Shortcut options",
      }] : []),
      ...moveMenuItem,
      ...(!file._is_shortcut ? ["separator"] : []),
      ...trashMenuItem,
      ...shareRemoveReportMenuItems
    ]
  });

  return (
    <ListItem icon="bullseye-arrow" iconColor="var(--rubric_file_color)" iconShortcut={file._is_shortcut} onClick={() => {filePicker ? filePicker.handleClick(file) : navigate("/rubrics/"+file.item_id)}} menu={menu} hideMenu={!!filePicker} disabled={filePicker && (filePicker.type !== "rubric" || filePicker.disable_files)}>
      <ItemColumn className={"with_share_icon" + ((file.trashed && view !== "trash") ? " crossed_out" : "")}><span>{file.title}</span>{file._is_shared && <Icon name="account-multiple-outline" className="is_shared_icon"/>}</ItemColumn>
      <ItemColumn className="item_shared_by">{file._date_shared && (<><Icon name="account-circle-outline" className="shared_by_icon"/><span>{(file._sharer_is_same_org ? file._sharer_name : file._sharer_email) || "(Deleted user)"}</span></>)}</ItemColumn>
      <ItemColumn>{(shortDate(file.date_created))}</ItemColumn>
      <ItemColumn>{(shortDate(file.date_modified))}</ItemColumn>
      <ItemColumn>{file._date_shared && (shortDate(file._date_shared))}</ItemColumn>
      <ItemColumn>{file.date_trashed && (shortDate(file.date_trashed))}</ItemColumn>
      <ItemColumn></ItemColumn>
      <ItemColumn>{view === "trash" && <OriginalLocation file={file} />}</ItemColumn>
      <ItemActions>{addToMyAction}</ItemActions>
    </ListItem>
  )
}

function ShortcutItem({file, view, filePicker, folder}) {
  const { moveMenuItem } = useMove({ file, folder, view });
  const { trashMenuItem } = useTrash({ file, folder, view });

  const menu = useMenu({
    menuItems: [
      {
        heading: "Shortcut options",
      },
      ...moveMenuItem,
      ...trashMenuItem,
    ]
  });

  return (
    <ListItem className="has_span_full broken_shortcut" icon={{"folder": "folder", "scenario": "forum-outline", "rubric": "bullseye-arrow"}[file.shortcut_file_type]} iconColor={{"folder": undefined, "scenario": "var(--scenario_file_color)", "rubric": "var(--rubric_file_color)"}[file.shortcut_file_type]} iconShortcut={true} menu={menu} hideMenu={!!filePicker} disabled={!!filePicker}>
      <ItemColumn className="item_span_full item_broken_shortcut_title"><div>{file.title}</div><div>{file._deleted ? `This ${file.shortcut_file_type} was deleted.` : `You need permission to view this ${file.shortcut_file_type}.`}</div></ItemColumn>
      <ItemColumn></ItemColumn>
      <ItemColumn></ItemColumn>
      <ItemColumn></ItemColumn>
      <ItemColumn></ItemColumn>
      <ItemColumn></ItemColumn>
      <ItemColumn></ItemColumn>
      <ItemColumn></ItemColumn>
      <ItemActions></ItemActions>
    </ListItem>
  )
}

export function useSharedWithMe({ file, is_shared, use_local_state = false, include_separator = true }) {
  const can_edit = ["edit", "owner"].includes(file?._level);

  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const [openErrorModal] = useErrorModal();
  const [locally_added, setLocallyAdded] = useState(false);
  const { uid } = useUser();

  const [openAddToMyModal] = useModal(function ({ context, closeModal, setLoading }) {
    function submitCreateShortcut({ formatted_data, focusError }) {
      setLoading(true);

      if (formatted_data.how === "shortcut") {
        publicAction("createShortcut", { id: file._id, parent: formatted_data.parent?._id || null }).then(function(data) {
          publicAction("shareFiles", {
            ids: [file._id],
            people: [{ _id: uid }],
            level: "edit",
            notify_people: false,
            message: null
          }).then(function() {
            closeModal();
            if (use_local_state) {
              setLocallyAdded("shortcut");
            } else {
              revalidator.revalidate();
            }
          }).catch(function(error) {
            console.error(error);
            setLoading(false);
            focusError({name: "parent", error: "Something went wrong, please try again."});
          });
        }).catch(function(error) {
          console.error(error);
          setLoading(false);
          focusError({name: "parent", error: "Something went wrong, please try again."});
        });
      } else {
        if (file.type === "scenario") {
          publicAction("duplicateScenario", { id: file.item_id, title: file.title, parent: formatted_data.parent?._id || null }).then(function({ data }) {
            const { item_id } = data;
            closeModal();
            if (use_local_state) {
              setLocallyAdded("copy");
            } else {
              navigate(`/scenarios/${item_id}`);
            }
          }).catch(function(error) {
            console.error(error);
            setLoading(false);
            focusError({name: "parent", error: "Something went wrong, please try again."});
          });
        } else if (file.type === "rubric") {
          publicAction("duplicateRubric", { id: file.item_id, title: file.title, parent: formatted_data.parent?._id || null }).then(function({ data }) {
            const { item_id } = data;
            closeModal();
            if (use_local_state) {
              setLocallyAdded("copy");
            } else {
              navigate(`/rubrics/${item_id}`);
            }
          }).catch(function(error) {
            console.error(error);
            setLoading(false);
            focusError({name: "parent", error: "Something went wrong, please try again."});
          });
        }
      }
    }

    return (
      <Modal title={`Add to my ${item_view}`}>
        <FormProvider onSubmit={submitCreateShortcut}>
          <ModalContent>
            {file.type !== "folder" && <>
              <RadioGroup title="Choose how to add" name="how" required="Please choose an option" default_value={can_edit ? "shortcut" : undefined}>
                <RadioOption name="shortcut" text={`Add a shortcut to the shared file${!can_edit ? " — you can only view" : " — you can edit"}`}/>
                <RadioOption name="copy" text={`Make my own copy${!can_edit ? " — so you can edit" : ""}`}/>
              </RadioGroup>
              <Spacing/>
            </>}
            <FileLocation name="parent" title="Choose where to save" type={item_view.substring(0, item_view.length - 1)}/>
          </ModalContent>
          <ModalButtons>
            <Button text="Cancel" className="hollow" onClick={closeModal}/>
            <FormSubmitProvider>
              {({handleSubmit}) => (
                <FormDataProvider fields={["how"]}>
                  {({data}) => (
                    <Button text={data.how === "copy" ? "Make a copy" : "Add shortcut"} className="primary" onClick={handleSubmit}/>
                  )}
                </FormDataProvider>
              )}
            </FormSubmitProvider>
          </ModalButtons>
        </FormProvider>
      </Modal>
    )
  })

  const [openRemoveMeModal] = useModal(function ({ context, closeModal, setLoading }) {
    function submitTrashRubric() {
      setLoading(true);
  
      publicAction("removeMeFromFile", { id: file._id }).then(function(data) {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title={`Remove yourself from this ${file.type}?`}>
        <ModalContent>
          {`The ${file.type} will be unshared with you.`+(file._added_shortcut_id ? ` The shortcut you added will stop working.` : "")}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Remove me" className="destructive" onClick={submitTrashRubric}/>
        </ModalButtons>
      </Modal>
    )
  })

  if (!(file?.view || file?.type)) {
    return {};
  }

  const item_view = file.view || (file.type+"s");
  const icon = item_view === "scenarios" ? "forum-plus-outline" : "bullseye-arrow-plus";

  const addToMyAction = is_shared && ((file._added_shortcut_id || file._added_copy_item_id) ? (
    <div className="shortcut_added_label"><Icon name="check"/><span>{`${file._added_shortcut_id ? "Added a shortcut" : "Made a copy"}`}</span></div>
  ) : (
    <Button backgroundColor={"var(--header)"} icon={icon} text={`Add to my ${item_view}`} className="small hollow" onClick={(event) => {event.stopPropagation(); openAddToMyModal()}} />
  ));

  const addToMyMenuItem = is_shared ? [
    ...((file._added_shortcut_id || file._added_copy_item_id || locally_added) ? [
      {
        label: `${(file._added_shortcut_id || locally_added === "shortcut") ? "Added a shortcut" : "Made a copy"}`,
        icon: "check",
        disabled: true
      }
    ] : [
      {
        label: `Add to my ${item_view}`,
        icon: icon,
        onClick: openAddToMyModal
      }
    ]),
    ...(include_separator ? ["separator"] : []),
  ] : []

  const shareRemoveReportMenuItems = is_shared ? [
    {
      label: `Remove me`,
      icon: "close",
      onClick: openRemoveMeModal
    },
    // {
    //   label: `Block and report`,
    //   icon: "alert-octagon-outline",
    //   onClick: () => {}
    // },
  ] : []

  return { addToMyAction, addToMyMenuItem, shareRemoveReportMenuItems, openAddToMyModal };
}

export function useMove({ file, folder, view }) {
  const [handleMove] = useMoveModal();
  const { uid } = useUser();

  // We need the parent folder in order to properly know if we have permission to move & to show move confirmation modal.

  // Because it's impossible to share shortcuts directly, the _level of a resolved shortcut is the same as the _level of the folder it's in. Unless we are the owner of the shortcut (which must be the case if the shortcut is at our root), in which case we have edit access to it. Unresolved shortcuts have a _level directly set.
  const has_edit_access_on_item = file && (
    file._is_shortcut ? (
      ["edit", "owner"].includes(folder?._level) || file._shortcut_uid === uid
    ) : (
      ["edit", "owner"].includes(file._level)
    )
  );

  const has_edit_access_on_parent = ["edit", "owner"].includes(folder?._level) || (file && ((file._shortcut_root_uid || file.root_uid) === uid || (file._shortcut_uid || file.uid) === uid));

  const can_move = has_edit_access_on_item && has_edit_access_on_parent && !file.trashed;

  const moveMenuItem = view !== "shared" ? [
    {
      label: 'Move',
      icon: 'folder-move-outline',
      onClick: () => {handleMove(file, folder)},
      disabled: !can_move,
    }
  ] : [];

  return { moveMenuItem };
}

export function useTrash({ file, folder, view, dont_show_remove_option = false, go_back_upon_trash = false }) {
  const { uid } = useUser();
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const [openErrorModal] = useErrorModal();

  const [handleTrash] = useModal(function ({ context, closeModal, setLoading }) {
    function handleSubmit() {
      setLoading(true);
  
      publicAction("trashFile", { id: file._shortcut_id || file._id }).then(function(data) {
        closeModal();
        if (go_back_upon_trash) {
          navigate(-1);
        } else {
          revalidator.revalidate();
        }
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title={`Move ${file.type} to trash?`}>
        <ModalContent>
          {`This ${file.type} will be deleted forever after 30 days. ${file._is_shared ? `People it's shared with will still have access until it's permanently deleted.` : ""}`}
          {(file._is_shared && file.type === "folder") ? <>
            <Spacing/>
            {`Only items inside this folder that you own will be permanently deleted. The rest will be relocated.`}
          </> : ""}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Move to trash" className="destructive" onClick={handleSubmit}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [handleRemove] = useModal(function ({ context, closeModal, setLoading }) {
    function handleSubmit() {
      setLoading(true);
  
      if (file._is_shortcut || file.type === "shortcut") {
        publicAction("deleteShortcut", { id: file._shortcut_id || file._id }).then(function(data) {
          closeModal();
          revalidator.revalidate();
        }).catch(function(error) {
          console.error(error);
          closeModal();
          openErrorModal();
        });
      } else {
        publicAction("removeFile", { id: file._id }).then(function(data) {
          closeModal();
          revalidator.revalidate();
        }).catch(function(error) {
          console.error(error);
          closeModal();
          openErrorModal();
        });
      }
    }

    return (
      <Modal title={`Remove ${file._is_shortcut ? "shortcut" : file.type}?`}>
        <ModalContent>
          {(file._is_shortcut || file.type === "shortcut") ? (
            `This shortcut will be removed. ${file.type === "shortcut" && file._deleted ? `The original ${file.shortcut_file_type} was already deleted.` : `The original ${file.shortcut_file_type || file.type} won't be deleted.`}`
          ) : (
            `This ${file.type} will be removed from view, but not deleted. You and others may lose access.`
          )}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Remove" className="primary" onClick={handleSubmit}/>
        </ModalButtons>
      </Modal>
    )
  })

  const is_item_owner = file?.uid === uid;
  const is_shortcut = file?._is_shortcut || file?.type === "shortcut";
  const has_edit_access_on_parent = ["edit", "owner"].includes(folder?._level) || (file && ((file._shortcut_root_uid || file.root_uid) === uid || (file._shortcut_uid || file.uid) === uid));

  const trashMenuItem = view !== "shared" ? (
    (is_item_owner && !is_shortcut) ? [
      {
        label: 'Move to trash',
        icon: 'trash-can-outline',
        disabled: file.trashed,
        onClick: handleTrash
      }
    ] : (!dont_show_remove_option ? [
      {
        label: 'Remove',
        icon: 'close',
        disabled: !has_edit_access_on_parent || (file.trashed && !is_shortcut),
        onClick: handleRemove
      }
    ] : [])
  ) : [];

  const [handleDelete] = useModal(function ({ context, closeModal, setLoading }) {
    function handleSubmit() {
      setLoading(true);
  
      publicAction("deleteFile", { id: file._id }).then(function(data) {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title={`Delete forever?`}>
        <ModalContent>
          {`This ${file.type} will be permanently deleted.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Delete" className="destructive" onClick={handleSubmit}/>
        </ModalButtons>
      </Modal>
    )
  })

  const handleRestore = () => {
    publicAction("untrashFile", { id: file._id }).then(function() {
      revalidator.revalidate();
    }).catch(function(error) {
      console.error(error);
      openErrorModal();
    });
  }

  const trashedMenuItems = view === "trash" && (
    [
      {
        label: 'Restore',
        icon: 'history',
        onClick: handleRestore,
      },
      {
        label: 'Delete forever',
        icon: 'delete-forever-outline',
        onClick: handleDelete
      },
    ]
  );

  return { trashMenuItem, trashedMenuItems };
}

const OriginalLocation = ({ file }) => {
  const { uid } = useUser();

  return (
    <div className="trash_original_location">
      {(file.parent && file._parent_title) ? (
        <>
          <Icon name={file._parent_is_shared ? "folder-account" : "folder"} />
          <span>{file._parent_title}</span>
        </>
      ) : (
        file.root_uid === uid ? (
          <>
            <Icon name={{"scenarios": "forum-outline", "rubrics": "bullseye-arrow"}[file.view || (file.type+"s")]} />
            <span>{`My ${file.view || (file.type+"s")}`}</span>
          </>
        ) : (
          <>
            <Icon name="account-multiple-outline" />
            <span>{`Shared with me`}</span>
          </>
        )
      )}
    </div>
  )
}

export const useNewFolderModal = () => {
  const [openNewFolderModal] = useModal(function ({ context, closeModal, setLoading }) {
    const { folder_id, view, callback } = context;

    function handleSubmit({formatted_data, focusError}) {
      setLoading(true);
  
      publicAction("createFolder", { parent: folder_id || null, title: formatted_data.title, view: view }).then(function({ data }) {
        closeModal();
        callback({ id: data.id });
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="New folder">
        <FormProvider onSubmit={handleSubmit}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="title" title="Folder title" onEnter={handleSubmit} required="Please enter a title" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  return [openNewFolderModal];
}

export function useHandleRestore({ file }) {
  const revalidator = useRevalidator();
  const [openErrorModal] = useErrorModal();

  const [is_restoring, setIsRestoring] = useState(false);

  const handleRestore = () => {
    setIsRestoring(true);
    publicAction("untrashFile", { id: file._id }).then(function() {
      setIsRestoring(false);
      revalidator.revalidate();
    }).catch(function(error) {
      console.error(error);
      openErrorModal();
      setIsRestoring(false);
    });
  }

  return { handleRestore, is_restoring };
}